import * as React from 'react';
import { useTheme } from '../../theme';
import { CSSRulesFunction } from '../../theme/types';
import { FontFamily } from '../../theme/tokens';
import { omit } from '../../utils';
import Icon, { IconNames, IconVariant } from '../Icon';
import { CSSInterpolation } from '@emotion/serialize';
import Space from '../layout/Space';
import IconButton from './IconButton';
import {
  colorVariantStyling,
  commonButtonStyle,
  unpackButtonColorVariantStyles,
  unpackSizeStyles,
} from './appearance';
import {
  AppearanceProps,
  ButtonColorVariant,
  ButtonSize,
  ButtonVariant,
  Props,
} from './types';

type HTMLProps = Omit<
  React.HTMLAttributes<HTMLButtonElement>,
  keyof AppearanceProps
>;

export interface ButtonProps extends AppearanceProps, Props, HTMLProps {}

const buttonStyle: CSSRulesFunction<AppearanceProps> = (theme, props) => {
  const {
    colorVariant = ButtonColorVariant.Primary,
    size = ButtonSize.Large,
  } = props;
  const variantColors = colorVariantStyling[colorVariant];
  const sizeStyles = unpackSizeStyles(size, theme);
  const fontFamily = theme.tokens.FontFamilies[FontFamily.SemiBold];

  const fontStyles = {
    fontSize: sizeStyles.fontSize,
    lineHeight: sizeStyles.lineHeight,
    fontFamily,
  };

  const width = theme.responsiveStyle(props.width, ['width']);
  const height = theme.responsiveStyle(props.height, ['height']);
  const maxWidth = theme.responsiveStyle(props.maxWidth, ['maxWidth']);
  const maxHeight = theme.responsiveStyle(props.maxHeight, ['height']);
  const paddingV = theme.responsiveSpacing(sizeStyles.paddingV, [
    'paddingTop',
    'paddingBottom',
  ]);
  const paddingH = theme.responsiveSpacing(sizeStyles.paddingH, [
    'paddingLeft',
    'paddingRight',
  ]);
  const baseButtonStyle = [
    width,
    maxWidth,
    maxHeight,
    height,
    paddingV,
    paddingH,
  ];

  const processedButtonStyle = {
    ...fontStyles,
    ...unpackButtonColorVariantStyles(variantColors.base, theme),
    '&:hover': {
      textDecoration: 'none',
      ...unpackButtonColorVariantStyles(variantColors.hover, theme),
    },
    '&:focus': {
      ...unpackButtonColorVariantStyles(variantColors.focus, theme),
    },
    '&:active': {
      ...unpackButtonColorVariantStyles(variantColors.active, theme),
    },
    // Think this should be based off disabled prop, not aria
    '&[aria-disabled=true]': {
      ...unpackButtonColorVariantStyles(variantColors.disabled, theme),
      cursor: 'not-allowed',
      pointerEvents: 'all',
    },
  } as CSSInterpolation;

  return [
    commonButtonStyle(theme, props),
    baseButtonStyle,
    processedButtonStyle,
  ] as CSSInterpolation;
};

const LeftButtonIcon: React.FC<{
  name: IconNames | undefined;
}> = ({ name }) => {
  if (!name) return null;

  return (
    <>
      <Icon name={name} size={4} variant={IconVariant.Inherit} />
      <Space size={1} direction="horizontal" />
    </>
  );
};

const RightButtonIcon: React.FC<{
  name: IconNames | undefined;
}> = ({ name }) => {
  if (!name) return null;

  return (
    <>
      <Space size={1} direction="horizontal" />
      <Icon name={name} size={4} variant={IconVariant.Inherit} />
    </>
  );
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  props,
  ref
) {
  const { theme } = useTheme();
  const rest = omit(props, [
    'colorVariant',
    'size',
    'width',
    'height',
    'maxWidth',
    'maxHeight',
    'beforeIcon',
    'afterIcon',
    'disabled',
    'children',
    'type',
    'icon',
  ]);
  const {
    children,
    disabled,
    beforeIcon,
    afterIcon,
    type,
    variant,
    colorVariant,
    icon,
    size,
  } = props;

  const buttonType = type || 'button';

  if (variant === ButtonVariant.Icon) {
    return (
      <IconButton
        {...rest}
        disabled={disabled}
        buttonType={buttonType}
        size={size}
        colorVariant={colorVariant}
        icon={icon}
      >
        {children}
      </IconButton>
    );
  }

  return (
    <button
      css={buttonStyle(theme, props)}
      aria-disabled={disabled}
      disabled={disabled}
      type={buttonType}
      {...rest}
      ref={ref}
    >
      <LeftButtonIcon name={beforeIcon} />
      {children}
      <RightButtonIcon name={afterIcon} />
    </button>
  );
});

export default Button;
